import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Save, Plus, Trash2, Clock, Image as ImageIcon, X } from 'lucide-react'
import { amenityApi } from '../services/api'
import api from '../services/api'
import { showError } from '../utils/showError'
import RichTextEditor from '../components/RichTextEditor'
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  Button,
  FormField,
  FormSelect,
  FormCheckbox,
  ErrorMessage,
  Badge
} from '../components/common'
import {
  AMENITY_TYPES,
  AMENITY_TYPE_OPTIONS,
  AMENITY_PRESETS,
} from '../utils/amenityConstants'

function formatTimeDuration(startTime, endTime) {
  if (!startTime || !endTime) return ''
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const totalMinutes = (eh * 60 + em) - (sh * 60 + sm)
  if (totalMinutes <= 0) return ''
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${minutes}m`
}

const CreateAmenity = () => {
  const navigate = useNavigate()
  const { amenityId } = useParams()
  const [searchParams] = useSearchParams()
  const queryBuildingId = searchParams.get('buildingId')
  const isEditing = !!amenityId

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [existingCoverUrl, setExistingCoverUrl] = useState(null)
  const [formData, setFormData] = useState({
    buildingId: queryBuildingId || '',
    name: '',
    rolesDescription: '',
    startTime: '',
    endTime: '',
    amenityType: 0,
    requiresConfirmation: true,
    maxAdvanceBookingDays: '',
    minAdvanceBookingHours: '',
    maxBookingsPerMemberPerMonth: '',
    maxCapacity: '',
    checkInExpiryHours: '',
    timeSlots: [],
    unavailableDates: [],
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadBuildings()
    if (isEditing) {
      loadAmenityForEdit()
    }
  }, [isEditing, amenityId])

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building', { params: { pageNumber: 1, pageSize: 200 } })
      setBuildings(response.data.items || [])
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadAmenityForEdit = async () => {
    try {
      setLoading(true)
      const response = await amenityApi.get(amenityId)
      const amenity = response.data?.data || response.data

      setFormData({
        buildingId: amenity.buildingId || '',
        name: amenity.name || '',
        rolesDescription: amenity.rolesDescription || '',
        startTime: amenity.startTime ? amenity.startTime.substring(0, 5) : '',
        endTime: amenity.endTime ? amenity.endTime.substring(0, 5) : '',
        amenityType: amenity.amenityType ?? 0,
        requiresConfirmation: amenity.requiresConfirmation ?? true,
        maxAdvanceBookingDays: amenity.maxAdvanceBookingDays ?? '',
        minAdvanceBookingHours: amenity.minAdvanceBookingHours ?? '',
        maxBookingsPerMemberPerMonth: amenity.maxBookingsPerMemberPerMonth ?? '',
        maxCapacity: amenity.maxCapacity ?? '',
        checkInExpiryHours: amenity.checkInExpiryHours ?? '',
        timeSlots: (amenity.timeSlots || []).map(s => ({
          amenityTimeSlotId: s.amenityTimeSlotId,
          name: s.name,
          startTime: s.startTime?.substring(0, 5) || '',
          endTime: s.endTime?.substring(0, 5) || '',
          displayOrder: s.displayOrder ?? 0,
        })),
        unavailableDates: (amenity.unavailableDates || []).map(ud => ({
          amenityUnavailableDateId: ud.amenityUnavailableDateId,
          from: ud.from ? ud.from.substring(0, 16) : '',
          to: ud.to ? ud.to.substring(0, 16) : '',
          reason: ud.reason || '',
        })),
      })
      if (amenity.defaultCoverUrl) {
        setExistingCoverUrl(amenity.defaultCoverUrl.startsWith('http') ? amenity.defaultCoverUrl : `https://${amenity.defaultCoverUrl}`)
      }
    } catch (error) {
      console.error('Error loading amenity:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.buildingId) {
      newErrors.buildingId = 'Building is required'
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be 200 characters or less'
    }
    if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
      newErrors.endTime = 'End time must be after start time'
    }
    if (formData.amenityType === AMENITY_TYPES.TIME_SLOT && formData.timeSlots.length === 0) {
      newErrors.timeSlots = 'At least one time slot is required for Time Slot type'
    }
    if (formData.amenityType === AMENITY_TYPES.TIME_SLOT) {
      formData.timeSlots.forEach((slot, i) => {
        if (slot.startTime && slot.endTime && slot.endTime <= slot.startTime) {
          newErrors[`timeSlot_${i}`] = `Slot "${slot.name || i + 1}": end time must be after start time`
        }
      })
    }
    if (formData.amenityType === AMENITY_TYPES.CAPACITY_LIMITED) {
      if (!formData.maxCapacity || parseInt(formData.maxCapacity) <= 0) {
        newErrors.maxCapacity = 'Max capacity must be greater than 0'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      name: preset.name,
      startTime: preset.startTime || '',
      endTime: preset.endTime || '',
      amenityType: preset.amenityType ?? 0,
      requiresConfirmation: preset.requiresConfirmation ?? true,
      maxAdvanceBookingDays: preset.maxAdvanceBookingDays ?? '',
      minAdvanceBookingHours: preset.minAdvanceBookingHours ?? '',
      maxBookingsPerMemberPerMonth: preset.maxBookingsPerMemberPerMonth ?? '',
      maxCapacity: preset.maxCapacity ?? '',
      checkInExpiryHours: preset.checkInExpiryHours ?? '',
    }))
  }

  // Time Slot management
  const addTimeSlot = () => {
    setFormData(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, { name: '', startTime: '', endTime: '', displayOrder: prev.timeSlots.length }],
    }))
  }

  const updateTimeSlot = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.timeSlots]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, timeSlots: updated }
    })
    if (errors.timeSlots) {
      setErrors(prev => ({ ...prev, timeSlots: undefined }))
    }
  }

  const removeTimeSlot = (index) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index),
    }))
  }

  // Unavailable Date management
  const addUnavailableDate = () => {
    setFormData(prev => ({
      ...prev,
      unavailableDates: [...prev.unavailableDates, { from: '', to: '', reason: '' }],
    }))
  }

  const updateUnavailableDate = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.unavailableDates]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, unavailableDates: updated }
    })
  }

  const removeUnavailableDate = (index) => {
    setFormData(prev => ({
      ...prev,
      unavailableDates: prev.unavailableDates.filter((_, i) => i !== index),
    }))
  }

  // Image handling
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 10MB' }))
      return
    }
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Only image files are allowed' }))
      return
    }
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setErrors(prev => ({ ...prev, image: undefined }))
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setSubmitting(true)

      const toIntOrNull = (v) => {
        const n = parseInt(v)
        return isNaN(n) ? null : n
      }

      const submitData = {
        buildingId: formData.buildingId,
        name: formData.name,
        rolesDescription: formData.rolesDescription || null,
        startTime: formData.startTime || null,
        endTime: formData.endTime || null,
        amenityType: formData.amenityType,
        requiresConfirmation: formData.requiresConfirmation,
        maxAdvanceBookingDays: toIntOrNull(formData.maxAdvanceBookingDays),
        minAdvanceBookingHours: toIntOrNull(formData.minAdvanceBookingHours),
        maxBookingsPerMemberPerMonth: toIntOrNull(formData.maxBookingsPerMemberPerMonth),
        maxCapacity: toIntOrNull(formData.maxCapacity),
        checkInExpiryHours: toIntOrNull(formData.checkInExpiryHours),
      }

      let savedAmenityId = amenityId

      if (isEditing) {
        // Build time slots for update (with upsert flags)
        submitData.amenityId = parseInt(amenityId)
        submitData.timeSlots = formData.timeSlots.map((s, i) => ({
          amenityTimeSlotId: s.amenityTimeSlotId || null,
          name: s.name,
          startTime: s.startTime || null,
          endTime: s.endTime || null,
          displayOrder: i,
          shouldDelete: false,
        }))
        submitData.unavailableDates = formData.unavailableDates.map(ud => ({
          amenityUnavailableDateId: ud.amenityUnavailableDateId || null,
          from: ud.from ? new Date(ud.from).toISOString() : null,
          to: ud.to ? new Date(ud.to).toISOString() : null,
          reason: ud.reason || null,
          shouldDelete: false,
        }))

        await amenityApi.update(submitData)
      } else {
        // Create with inline time slots and unavailable dates
        submitData.timeSlots = formData.timeSlots.map((s, i) => ({
          name: s.name,
          startTime: s.startTime || null,
          endTime: s.endTime || null,
          displayOrder: i,
        }))
        submitData.unavailableDates = formData.unavailableDates
          .filter(ud => ud.from && ud.to)
          .map(ud => ({
            from: new Date(ud.from).toISOString(),
            to: new Date(ud.to).toISOString(),
            reason: ud.reason || null,
          }))

        const response = await amenityApi.create(submitData)
        const created = response.data?.data || response.data
        savedAmenityId = created.amenityId
      }

      // Upload image if selected
      if (imageFile && savedAmenityId) {
        const fd = new FormData()
        fd.append('file', imageFile)
        await amenityApi.uploadImage(savedAmenityId, fd)
      }

      navigate(`/amenities/${savedAmenityId}?buildingId=${formData.buildingId}`)
    } catch (error) {
      console.error('Error saving amenity:', error)
      showError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const showBookingRules = formData.amenityType === AMENITY_TYPES.BOOKABLE || formData.amenityType === AMENITY_TYPES.TIME_SLOT
  const showTimeSlots = formData.amenityType === AMENITY_TYPES.TIME_SLOT
  const showCapacity = formData.amenityType === AMENITY_TYPES.CAPACITY_LIMITED

  if (loading) return <LoadingSpinner />

  return (
    <DetailPageLayout
      title={isEditing ? 'Edit Amenity' : 'Create Amenity'}
      backLink={{ label: 'Back to Amenities', href: '/amenities' }}
      actions={[
        <Button
          key="save"
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
          icon={<Save className="h-4 w-4" />}
          loading={submitting}
        >
          {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </Button>
      ]}
    >
      <div className="space-y-6">
        {/* Quick Setup Presets (create only) */}
        {!isEditing && (
          <Card>
            <CardHeader title="Quick Setup" subtitle="Start from a preset to save time" />
            <CardBody>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {AMENITY_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="p-3 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
                  >
                    <div className="text-sm font-medium text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {preset.startTime} - {preset.endTime}
                    </div>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Basic Details */}
        <Card>
          <CardHeader title="Basic Details" />
          <CardBody className="space-y-6">
            <FormSelect
              label="Building"
              name="buildingId"
              value={formData.buildingId}
              onChange={(e) => handleInputChange('buildingId', e.target.value)}
              error={errors.buildingId}
              disabled={isEditing}
              options={[
                { value: '', label: 'Select a building' },
                ...buildings.map(b => ({
                  value: b.id,
                  label: b.name || b.userName
                }))
              ]}
              required
            />

            <FormField
              label="Name"
              name="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              placeholder="Enter amenity name"
              maxLength={200}
              helpText={`${formData.name.length}/200 characters`}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Start Time"
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
              />

              <FormField
                label="End Time"
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                error={errors.endTime}
              />
            </div>
            {formData.startTime && formData.endTime && formData.endTime > formData.startTime && (
              <p className="text-sm text-gray-500 mt-1">
                Duration: {formatTimeDuration(formData.startTime, formData.endTime)}
              </p>
            )}
          </CardBody>
        </Card>

        {/* Amenity Type */}
        <Card>
          <CardHeader title="Amenity Type" subtitle="How members interact with this amenity" />
          <CardBody>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AMENITY_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleInputChange('amenityType', opt.value)}
                  className={`p-4 border-2 rounded-lg text-left transition-colors ${
                    formData.amenityType === opt.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{opt.description}</div>
                </button>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Booking Rules */}
        {showBookingRules && (
          <Card>
            <CardHeader title="Booking Rules" subtitle="Configure booking behavior" />
            <CardBody className="space-y-6">
              <FormCheckbox
                label="Requires confirmation"
                name="requiresConfirmation"
                checked={formData.requiresConfirmation}
                onChange={(e) => handleInputChange('requiresConfirmation', e.target.checked)}
                helpText="When enabled, a manager must approve each booking"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormField
                  label="Max advance booking (days)"
                  name="maxAdvanceBookingDays"
                  type="number"
                  value={formData.maxAdvanceBookingDays}
                  onChange={(e) => handleInputChange('maxAdvanceBookingDays', e.target.value)}
                  placeholder="e.g. 14"
                  helpText="How far ahead members can book"
                />

                <FormField
                  label="Min notice (hours)"
                  name="minAdvanceBookingHours"
                  type="number"
                  value={formData.minAdvanceBookingHours}
                  onChange={(e) => handleInputChange('minAdvanceBookingHours', e.target.value)}
                  placeholder="e.g. 2"
                  helpText="Minimum hours before booking start"
                />

                <FormField
                  label="Max bookings per member / month"
                  name="maxBookingsPerMemberPerMonth"
                  type="number"
                  value={formData.maxBookingsPerMemberPerMonth}
                  onChange={(e) => handleInputChange('maxBookingsPerMemberPerMonth', e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
            </CardBody>
          </Card>
        )}

        {/* Time Slots (TimeSlot type only) */}
        {showTimeSlots && (
          <Card>
            <CardHeader
              title="Time Slots"
              subtitle="Define named booking slots"
              action={
                <Button
                  onClick={addTimeSlot}
                  variant="secondary"
                  size="small"
                  icon={<Plus className="w-3 h-3" />}
                >
                  Add Slot
                </Button>
              }
            />
            <CardBody>
              {errors.timeSlots && (
                <div className="mb-4">
                  <ErrorMessage message={errors.timeSlots} />
                </div>
              )}
              {formData.timeSlots.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No time slots added yet. Click "Add Slot" to create one.</p>
              ) : (
                <div className="space-y-3">
                  {formData.timeSlots.map((slot, index) => (
                    <div key={index}>
                      <div className="flex items-end gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                          <input
                            type="text"
                            value={slot.name}
                            onChange={(e) => updateTimeSlot(index, 'name', e.target.value)}
                            placeholder="e.g. Morning Session"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                              errors[`timeSlot_${index}`] ? 'border-red-300' : 'border-gray-200'
                            }`}
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                              errors[`timeSlot_${index}`] ? 'border-red-300' : 'border-gray-200'
                            }`}
                          />
                        </div>
                        <Button
                          onClick={() => removeTimeSlot(index)}
                          variant="ghost"
                          size="small"
                          icon={<Trash2 className="w-4 h-4 text-red-500" />}
                        />
                      </div>
                      {slot.startTime && slot.endTime && slot.endTime > slot.startTime && (
                        <p className="text-xs text-gray-500 mt-1 ml-3">
                          Duration: {formatTimeDuration(slot.startTime, slot.endTime)}
                        </p>
                      )}
                      {errors[`timeSlot_${index}`] && (
                        <p className="text-xs text-red-500 mt-1 ml-3">{errors[`timeSlot_${index}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Capacity Settings (CapacityLimited type only) */}
        {showCapacity && (
          <Card>
            <CardHeader title="Capacity Settings" />
            <CardBody className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Maximum Capacity"
                  name="maxCapacity"
                  type="number"
                  value={formData.maxCapacity}
                  onChange={(e) => handleInputChange('maxCapacity', e.target.value)}
                  error={errors.maxCapacity}
                  placeholder="e.g. 10"
                  helpText="Maximum number of people at once"
                  required
                />

                <FormField
                  label="Check-in Expiry (hours)"
                  name="checkInExpiryHours"
                  type="number"
                  value={formData.checkInExpiryHours}
                  onChange={(e) => handleInputChange('checkInExpiryHours', e.target.value)}
                  placeholder="e.g. 1"
                  helpText="Hours until check-in expires automatically"
                />
              </div>
            </CardBody>
          </Card>
        )}

        {/* Cover Image */}
        <Card>
          <CardHeader title="Cover Image" subtitle="Optional cover image for this amenity" />
          <CardBody>
            {errors.image && (
              <div className="mb-4">
                <ErrorMessage message={errors.image} />
              </div>
            )}
            <div className="flex items-start gap-6">
              {(imagePreview || existingCoverUrl) && (
                <div className="relative">
                  <img
                    src={imagePreview || existingCoverUrl}
                    alt="Cover preview"
                    className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              <div>
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <span>{imagePreview || existingCoverUrl ? 'Change Image' : 'Choose Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">JPEG, PNG, GIF or WebP. Max 10MB.</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Rules & Conditions */}
        <Card>
          <CardHeader title="Rules & Conditions" subtitle="Display rules and usage conditions to members" />
          <CardBody>
            <RichTextEditor
              content={formData.rolesDescription || ''}
              onChange={(html) => handleInputChange('rolesDescription', html)}
              placeholder="Enter amenity rules and conditions..."
            />
          </CardBody>
        </Card>

        {/* Unavailable Dates */}
        <Card>
          <CardHeader
            title="Unavailable Dates"
            subtitle="Block out dates when this amenity is not available"
            action={
              <Button
                onClick={addUnavailableDate}
                variant="secondary"
                size="small"
                icon={<Plus className="w-3 h-3" />}
              >
                Add Date
              </Button>
            }
          />
          <CardBody>
            {formData.unavailableDates.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No unavailable dates. Click "Add Date" to add one.</p>
            ) : (
              <div className="space-y-3">
                {formData.unavailableDates.map((ud, index) => (
                  <div key={index} className="flex items-end gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                      <input
                        type="datetime-local"
                        value={ud.from}
                        onChange={(e) => updateUnavailableDate(index, 'from', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                      <input
                        type="datetime-local"
                        value={ud.to}
                        onChange={(e) => updateUnavailableDate(index, 'to', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                      <input
                        type="text"
                        value={ud.reason}
                        onChange={(e) => updateUnavailableDate(index, 'reason', e.target.value)}
                        placeholder="e.g. Maintenance"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <Button
                      onClick={() => removeUnavailableDate(index)}
                      variant="ghost"
                      size="small"
                      icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </DetailPageLayout>
  )
}

export default CreateAmenity
